import type { Game } from '@/types/games';
import type {
    EmbeddedSearchDocumentType,
    EmbeddedSearchHit,
    SearchHit,
    SlimSearchGameData,
    SlimSearchRichText
} from '@/lib/search-hit';

const EMBEDDED_SEARCH_DOCUMENT_TYPES = new Set<EmbeddedSearchDocumentType>(['game', 'game_lobby']);

const hasDisplayName = (displayName: SlimSearchRichText | undefined): boolean => {
    if (typeof displayName === 'string') {
        return displayName.length > 0;
    }

    return Boolean(displayName?.length && displayName[0]?.text);
};

const hasProviderName = (data: SlimSearchGameData): boolean => {
    const providerName = data.provider?.data?.providername;

    if (typeof providerName === 'string') {
        return providerName.length > 0;
    }

    if (Array.isArray(providerName)) {
        return Boolean(providerName.length && providerName[0]?.text);
    }

    return Boolean(providerName);
};

const isEmbeddedSearchDocumentType = (type: unknown): type is EmbeddedSearchDocumentType =>
    typeof type === 'string' && EMBEDDED_SEARCH_DOCUMENT_TYPES.has(type as EmbeddedSearchDocumentType);

export const isEmbeddedSearchHit = (hit: SearchHit): hit is EmbeddedSearchHit => {
    if (!('uid' in hit) || !hit.uid || !('id' in hit) || !hit.id || !('type' in hit) || !('data' in hit)) {
        return false;
    }

    if (!isEmbeddedSearchDocumentType(hit.type)) {
        return false;
    }

    return hasDisplayName(hit.data.display_name) && hasProviderName(hit.data);
};

/** Maps a validated embedded search hit to the Game shape consumed by tiles and launch. */
export const adaptEmbeddedSearchHitToGame = (hit: EmbeddedSearchHit): Game =>
    ({
        id: hit.id,
        uid: hit.uid,
        type: hit.type,
        data: hit.data,
        ...(hit.rank_order !== undefined ? { rank_order: hit.rank_order } : {})
    }) as Game;

export const resolveGameFromHit = (hit: SearchHit, catalog: Map<string, Game>): Game | undefined => {
    if (isEmbeddedSearchHit(hit)) {
        return adaptEmbeddedSearchHitToGame(hit);
    }

    return catalog.get(hit._id);
};

export type DiscardedSearchHit = {
    hitId: string;
    hasUid: boolean;
    hasDisplayName: boolean;
};

export const mapSearchHitsToGames = (
    hits: SearchHit[],
    catalog: Map<string, Game>
): { games: Game[]; discardedHits: DiscardedSearchHit[] } => {
    const games: Game[] = [];
    const discardedHits: DiscardedSearchHit[] = [];

    for (const hit of hits) {
        const game = resolveGameFromHit(hit, catalog);
        if (game) {
            games.push(game);
            continue;
        }

        discardedHits.push({
            hitId: hit._id,
            hasUid: 'uid' in hit && Boolean(hit.uid),
            hasDisplayName: 'data' in hit && hasDisplayName(hit.data.display_name)
        });
    }

    return { games, discardedHits };
};
